import { es, t, type StringKey } from '../../i18n/es';
import type { BriefingModel } from './briefing-model';

export interface BriefingScreenOptions {
  model: BriefingModel;
  onDeploy: () => void;
  onBack: () => void;
}

const has = (key: string): key is StringKey => key in es;
const threatName = (type: string) => {
  const key = `enemy.${type}.name`;
  return has(key) ? `${type} ${t(key)}` : type;
};

/**
 * BRIEFING (GDD §13.2, issue #57): tactical label, title, route map, known threats and the message of
 * Mr. Smith, all derived from the level data. "Desplegar" opens the "Confirmar despliegue" dialog, which
 * traps the focus; Escape or "Cancelar" returns the focus to "Desplegar". Nothing here uses the critical
 * red: deploying is not destructive. Native elements until the design system lands (#15).
 */
export function briefingScreen({ model, onDeploy, onBack }: BriefingScreenOptions): HTMLElement {
  const name = t(`mission.${model.levelId}.name`).toUpperCase();
  const smithKey = `voice.smith.${model.levelId}`;
  const message = has(smithKey) ? t(smithKey) : null;
  const ending = model.ending ? t(`ui.briefing.ending.${model.ending}`) : null;

  const section = document.createElement('section');
  section.className = 'panel briefing';
  section.setAttribute('aria-labelledby', 'briefing-title');
  section.innerHTML = `
    <p class="tactical" data-field="op">${model.op}</p>
    <h1 id="briefing-title" class="display-mono" data-field="name">${name}</h1>
    <div class="rule"></div>
    <h2 class="tactical">${t('ui.briefing.route')}</h2>
    <div class="route" data-field="route" role="img" aria-label="${t('ui.briefing.route')}">
      <span class="route-start readout">${t('ui.briefing.route.start')}</span>
      <div class="route-bar">${model.marks
        .map((mark) => `<span class="route-mark readout" data-mark="${mark.id}" style="left: ${(mark.at * 100).toFixed(2)}%">${mark.id}</span>`)
        .join('')}</div>
      ${ending ? `<span class="route-end readout" data-field="route-end">${ending}</span>` : ''}
    </div>
    ${model.resume ? `<p class="readout" data-field="resume">${t('ui.briefing.resume', { checkpoint: model.resume })}</p>` : ''}
    <h2 class="tactical">${t('ui.briefing.threats')}</h2>
    <ul class="threats" data-field="threats">${
      model.threats.length
        ? model.threats.map((type) => `<li class="readout">${threatName(type)}</li>`).join('')
        : `<li class="muted">${t('ui.briefing.threats.none')}</li>`
    }</ul>
    ${
      message
        ? `<figure class="message" data-field="message">
        <figcaption class="tactical">${t('ui.briefing.message')} · <span data-field="sender">${t('ui.briefing.sender')}</span></figcaption>
        <blockquote class="body" data-field="smith" data-key="${smithKey}">${message}</blockquote>
      </figure>`
        : ''
    }
    ${model.deployable ? '' : `<p class="muted">${t('ui.briefing.deploy.unavailable')}</p>`}
    <div class="slot-actions">
      <button type="button" class="button" data-action="back">${t('ui.briefing.back')}</button>
      <button type="button" class="button" data-action="deploy"${model.deployable ? '' : ' disabled'}>${t('ui.briefing.deploy')}</button>
    </div>`;

  const deploy = section.querySelector<HTMLButtonElement>('[data-action="deploy"]')!;
  const back = section.querySelector<HTMLButtonElement>('[data-action="back"]')!;

  const confirm = () => {
    const dialog = document.createElement('dialog');
    dialog.className = 'dialog';
    dialog.setAttribute('aria-labelledby', 'deploy-title');
    dialog.innerHTML = `
      <h2 id="deploy-title" class="panel-title">${t('ui.briefing.deploy.dialog')}</h2>
      <p class="body">${t('ui.briefing.deploy.body', { op: model.op })}</p>
      <div class="slot-actions">
        <button type="button" class="button" data-choice="cancel">${t('ui.briefing.deploy.cancel')}</button>
        <button type="button" class="button" data-choice="confirm">${t('ui.briefing.deploy.confirm')}</button>
      </div>`;
    const close = (deployed: boolean) => {
      if (!dialog.open) return;
      dialog.close();
      dialog.remove();
      if (deployed) onDeploy();
      else deploy.focus();
    };
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      close(false);
    });
    dialog.querySelector('[data-choice="cancel"]')!.addEventListener('click', () => close(false));
    dialog.querySelector('[data-choice="confirm"]')!.addEventListener('click', () => close(true));
    section.append(dialog);
    dialog.showModal();
    dialog.querySelector<HTMLElement>('[data-choice="confirm"]')!.focus();
  };

  deploy.addEventListener('click', confirm);
  back.addEventListener('click', onBack);
  queueMicrotask(() => (model.deployable ? deploy : back).focus());
  return section;
}
